import { Sprite, Texture } from 'pixi.js';
import { Widget, Button, useSkin, type WidgetOptions } from './widget';
import { CELL, spriteName, type Sprites } from '../assets';
import { itemsById } from '../api';
let sprites: Sprites;
export function useSprites(s: Sprites): void { sprites = s; useSkin(s); }
export function spritesRef(): Sprites { return sprites; }
export class Icon extends Widget {
  constructor(key: string, size = 16, frame = 0, options: WidgetOptions = {}, skinIcon = false) {
    super('icon', options); this.style.width = `${size}px`; this.style.height = `${size}px`; this.style.pointerEvents = 'none';
    if (skinIcon) { const image = new Sprite(sprites.uiCell(key)); image.width = image.height = size; this.visualLayer.addChild(image); }
    else {
      const dimensions = sprites.sizes.get(spriteName(key)) ?? { w: 1, h: 1 };
      this.style.height = `${size * dimensions.h / dimensions.w}px`;
      sprites.frame(key, frame).forEach((texture, i) => { const image = new Sprite(texture); image.width = image.height = size / dimensions.w;
        image.position.set(i % dimensions.w * image.width, Math.floor(i / dimensions.w) * image.height); this.visualLayer.addChild(image); });
    }
  }
}
/** A chat emoji at a size in art pixels (smooth, so any size). */
export class EmojiIcon extends Widget {
  constructor(name: string, size: number) {
    super('icon'); this.style.width = this.style.height = `${size}px`; this.style.pointerEvents = 'none';
    const image = new Sprite(sprites.emoji(name)); image.width = image.height = size; this.visualLayer.addChild(image);
  }
}
export class IconButton extends Button {
  constructor(icon: string, title: string, action: () => void, options: WidgetOptions = {}) {
    super(options); this.title = title; this.testId = options.testId ?? `menu-${icon}`;
    this.append(new Icon(`icon_${icon}`, 16, 0, {}, true)); this.addEventListener('click', action);
  }
}
export class CharacterIcon extends Widget {
  private layers: Sprite[] = [];
  constructor(appearance: number, size: number, direction = 0, frame = 0, armor: number[] = []) {
    super('character'); this.style.width = this.style.height = `${size}px`; this.style.pointerEvents = 'none'; this.setAppearance(appearance, direction, frame, armor);
  }
  setAppearance(appearance: number, direction = 0, frame = 0, armor: number[] = []): void {
    const textures = [sprites.character(appearance, direction, frame)];
    for (const slot of [3, 2, 1, 0]) { const item = itemsById.get(armor[slot] ?? 0); if (!item || item.armor < 0) continue;
      const layer = sprites.armorLayer(`${item.look}_${['head', 'chest', 'legs', 'feet'][item.armor]}`, direction, frame); if (layer) textures.push(layer); }
    while (this.layers.length > textures.length) this.layers.pop()!.destroy();
    textures.forEach((texture, i) => { const sprite = this.layers[i] ?? new Sprite(); if (!this.layers[i]) { this.layers.push(sprite); this.visualLayer.addChild(sprite); } sprite.texture = texture; });
    this.invalidate();
  }
  override arrange(x: number, y: number, w: number, h: number): void { super.arrange(x, y, w, h); for (const layer of this.layers) { layer.width = w; layer.height = h; } }
}
/** An off-screen map raster uploaded to Pixi. This canvas is never mounted in the page. */
export class CanvasImage extends Widget {
  readonly surface = document.createElement('canvas');
  private texture = Texture.from(this.surface, true);
  private image = new Sprite(this.texture);
  private changed = true;
  constructor(options: WidgetOptions = {}) { super('raster', options); this.visualLayer.addChild(this.image); this.image.eventMode = 'none'; }
  get surfaceWidth(): number { return this.surface.width; }
  set surfaceWidth(v: number) { if (this.surface.width !== v) { this.surface.width = v; this.changed = true; } }
  get surfaceHeight(): number { return this.surface.height; }
  set surfaceHeight(v: number) { if (this.surface.height !== v) { this.surface.height = v; this.changed = true; } }
  getContext(_type: '2d'): CanvasRenderingContext2D { this.changed = true; return this.surface.getContext('2d')!; }
  override arrange(x: number, y: number, w: number, h: number): void { super.arrange(x, y, w, h); this.image.width = w; this.image.height = h; }
  override tick(now: number): void {
    super.tick(now);
    if (this.changed) {
      if (this.texture.width !== this.surface.width || this.texture.height !== this.surface.height) {
        const old = this.texture; this.texture = Texture.from(this.surface, true); this.image.texture = this.texture; old.destroy(true);
      } else this.texture.source.update();
      this.image.width = this.boxWidth; this.image.height = this.boxHeight;
      this.changed = false;
    }
  }
  override dispose(): void { if (this.destroyed) return; this.texture.destroy(true); super.dispose(); }
}
export function spriteIcon(key: string, size: number, frame = 0, extraClass = ''): Icon { return new Icon(key, size, frame, { class: extraClass }); }
export function itemIcon(item: number, size: number): Icon { return spriteIcon(itemsById.get(item)?.icon ?? '?missing', size); }
export function characterIcon(appearance: number, size: number, direction = 0, frame = 0, armor: number[] = []): CharacterIcon { return new CharacterIcon(appearance, size, direction, frame, armor); }
